import { useNotification } from "@expert/common-ui";
import { useCustomerAvatar } from "@expert/contentful";
import { Group, Stack, Text } from "@mantine/core";
import { type JSX } from "react";
import { useTimer } from "react-timer-hook";

import { ConnectCallbackTimeoutError } from "@/amazon-connect/withCallbacks";
import { PhoneNumber } from "@/common-ui";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useAgentSdk, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useCallbackStore } from "@/sdk/sessions/callback.store";
import { useAsyncLoading } from "@/shared-utils";

import classes from "./CallbackCallBar.module.css";
import { CallbackCallBarBody } from "./CallbackCallBarBody";

export function CallbackCallBar(): JSX.Element {
    const logger = useLogger({ module: "CallbackCallBar" });
    const session = useSession();
    const agentSdk = useAgentSdk();

    const { callbackState, setCallbackSuccessful } = useCallbackStore();
    const notification = useNotification();
    const [isCalling, handleCallCustomerBack] = useAsyncLoading(async () => {
        if (!callbackState) {
            throw new Error("CallbackCallBar should not be rendered without a callback state");
        }

        try {
            await agentSdk.callCustomerBack(callbackState);
            logger.info("Callback was successful");
            setCallbackSuccessful();
        } catch (error) {
            if (error instanceof ConnectCallbackTimeoutError) {
                notification.error(
                    "The call is taking longer than expected to complete. Please refresh the page and try again.",
                );
                logger.warn({ error }, "With callbacks timed out while calling customer back.");
            } else {
                logger.warn({ error }, "Error calling customer back");
            }
        }
    });

    const customerAvatarUrl = useCustomerAvatar(session.id);

    if (!callbackState) {
        throw new Error("CallbackCallBar should not be rendered without a callback state");
    }

    const expiryTimestamp = new Date(callbackState.scheduledFor);
    const { totalSeconds, isRunning, start } = useTimer({
        expiryTimestamp,
        autoStart: false,
        onExpire: () => {
            void handleCallCustomerBack();
        },
    });

    if (callbackState.scheduledFor && !isRunning && callbackState.scheduledFor > 0) {
        start();
    } else if (callbackState.scheduledFor === 0) {
        void handleCallCustomerBack();
    }

    const mdnText = callbackState.callbackMDN;

    const isCallingState =
        isCalling ||
        (callbackState.callbackType === "CallbackNow" && (callbackState.callbackDelay === 0 || totalSeconds === 0));

    return (
        <Group p={0}>
            {isCallingState ? (
                <Stack gap="0" p={0}>
                    <Text fw={300} size="xs" className={sharedStyles.animatedEllipses}>
                        Calling
                    </Text>
                    <Text className="fs-mask" fw={700} mr="sm" size="sm">
                        <PhoneNumber mdn={mdnText} />
                    </Text>
                </Stack>
            ) : (
                <>
                    <Group align="center" flex="1">
                        {customerAvatarUrl ? (
                            <img
                                className={classes.profilePicture}
                                src={customerAvatarUrl}
                                alt="customer profile avatar"
                            />
                        ) : null}

                        <CallbackCallBarBody mdnText={mdnText} secondsTillCall={totalSeconds} errorCalling={false} />
                    </Group>
                </>
            )}
        </Group>
    );
}
